import React, { useState, useEffect } from 'react';
import { getRoles, createRole, updateRole, deleteRole } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Shield, Plus, Pencil, Trash2, Loader2, RefreshCw, Lock } from 'lucide-react';
import { toast } from 'sonner';

// ── Permission matrix ──────────────────────────────────────────────────────────

const PERMISSION_GROUPS = [
  {
    label: 'POS-Aktionen',
    perms: [
      { value: 'bonieren',           label: 'Bonieren' },
      { value: 'offene_bestellungen',label: 'Offene Bestellungen' },
      { value: 'storno',             label: 'Storno' },
      { value: 'zahlen',             label: 'Zahlen' },
      { value: 'tisch_wechsel',      label: 'Tischwechsel' },
      { value: 'preis_anpassen',     label: 'Preis anpassen' },
      { value: 'rabatt_geben',       label: 'Rabatt geben' },
      { value: 'gang_abschicken',    label: 'Gang abschicken' },
      { value: 'bestellung_drucken', label: 'Bestellung drucken' },
    ],
  },
  {
    label: 'Zahlarten',
    perms: [
      { value: 'zahlung_bar',          label: 'Barzahlung' },
      { value: 'zahlung_karte',        label: 'Kartenzahlung' },
      { value: 'zahlung_gutschein',    label: 'Gutschein / Kundenkarte' },
      { value: 'zahlung_intern',       label: 'Interne Buchung' },
      { value: 'zahlung_ueberweisung', label: 'Überweisung' },
    ],
  },
  {
    label: 'Berichte',
    perms: [
      { value: 'berichte_sehen',               label: 'Berichte einsehen' },
      { value: 'berichte_exportieren',          label: 'Berichte exportieren' },
      { value: 'kassenabschluss_sehen',         label: 'Kassenabschluss einsehen' },
      { value: 'kassenabschluss_durchfuehren',  label: 'Kassenabschluss durchführen' },
    ],
  },
  {
    label: 'Verwaltung',
    perms: [
      { value: 'benutzer_verwalten',     label: 'Benutzer verwalten' },
      { value: 'artikel_verwalten',      label: 'Artikel verwalten' },
      { value: 'kunden_verwalten',       label: 'Kunden verwalten' },
      { value: 'gutscheine_verwalten',   label: 'Gutscheine verwalten' },
      { value: 'lager_verwalten',        label: 'Lager verwalten' },
      { value: 'kassenbuch_verwalten',   label: 'Kassenbuch verwalten' },
      { value: 'einstellungen_verwalten',label: 'Einstellungen verwalten' },
      { value: 'tische_verwalten',       label: 'Tische verwalten' },
    ],
  },
  {
    label: 'Kasse',
    perms: [
      { value: 'kassa_oeffnen',         label: 'Kasse öffnen' },
      { value: 'kassa_schliessen',      label: 'Kasse schließen' },
      { value: 'nullbeleg_erstellen',   label: 'Nullbeleg erstellen' },
      { value: 'jahresbeleg_erstellen', label: 'Jahresbeleg erstellen' },
    ],
  },
];

const PRESET_COLORS = [
  '#1a2f38','#2563eb','#7c3aed','#0891b2','#059669','#d97706','#dc2626','#64748b',
];

const emptyForm = { name: '', color: '#1a2f38', permissions: [] };

function PermissionChips({ permissions }) {
  const count = permissions.length;
  if (!count) return <span className="text-xs text-slate-400">Keine Berechtigungen</span>;
  const first3 = permissions.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1">
      {first3.map(p => (
        <span key={p} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
          {p.replace(/_/g, ' ')}
        </span>
      ))}
      {count > 3 && (
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
          +{count - 3}
        </span>
      )}
    </div>
  );
}

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      setRoles(await getRoles());
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (role = null) => {
    setEditing(role);
    setForm(role
      ? { name: role.name, color: role.color || '#1a2f38', permissions: role.permissions || [] }
      : emptyForm
    );
    setShowDialog(true);
  };

  const togglePerm = (perm) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter(p => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const toggleGroup = (group) => {
    const groupPerms = group.perms.map(p => p.value);
    const allSelected = groupPerms.every(p => form.permissions.includes(p));
    setForm(f => ({
      ...f,
      permissions: allSelected
        ? f.permissions.filter(p => !groupPerms.includes(p))
        : [...new Set([...f.permissions, ...groupPerms])],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Bitte Rollenname eingeben'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateRole(editing.id, form);
        toast.success('Rolle aktualisiert');
      } else {
        await createRole(form);
        toast.success('Rolle erstellt');
      }
      setShowDialog(false);
      load();
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Rolle "${role.name}" wirklich löschen?`)) return;
    try {
      await deleteRole(role.id);
      toast.success('Rolle gelöscht');
      load();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Rollen & Berechtigungen</h1>
          <p className="text-slate-500">{roles.length} Rollen</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => openDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Neue Rolle
          </Button>
        </div>
      </div>

      {roles.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-4">Noch keine Rollen angelegt</p>
            <Button className="bg-[#1a2f38]" onClick={() => openDialog()}>
              Erste Rolle erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => (
            <Card key={role.id} className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: role.color || '#1a2f38' }}
                    />
                    <span className="font-semibold text-[#1a2f38]">{role.name}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(role)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => handleDelete(role)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-slate-400 mb-2">
                  {(role.permissions || []).length} Berechtigungen
                </div>
                <PermissionChips permissions={role.permissions || []} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Role Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Rolle bearbeiten' : 'Neue Rolle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Name + Color */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="z.B. Kellner"
                  autoFocus
                />
              </div>
              <div>
                <Label>Farbe</Label>
                <div className="flex gap-2 mt-1">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-6 h-6 rounded-full border-2 transition-transform ${form.color === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Permission matrix */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold">Berechtigungen</Label>
                <span className="text-xs text-slate-400">{form.permissions.length} ausgewählt</span>
              </div>
              <div className="space-y-4">
                {PERMISSION_GROUPS.map(group => {
                  const groupPerms = group.perms.map(p => p.value);
                  const allSelected = groupPerms.every(p => form.permissions.includes(p));
                  const someSelected = groupPerms.some(p => form.permissions.includes(p));
                  return (
                    <div key={group.label}>
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => toggleGroup(group)}
                          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                            allSelected ? 'bg-[#1a2f38] border-[#1a2f38]' :
                            someSelected ? 'bg-slate-300 border-slate-400' :
                            'border-slate-300'
                          }`}
                        >
                          {allSelected && <span className="text-white text-[10px] font-bold">✓</span>}
                          {someSelected && !allSelected && <span className="text-white text-[10px] font-bold">–</span>}
                        </button>
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {group.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 ml-6">
                        {group.perms.map(p => {
                          const checked = form.permissions.includes(p.value);
                          return (
                            <label
                              key={p.value}
                              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                                checked ? 'bg-[#1a2f38]/5 border-[#1a2f38]/30' : 'border-slate-100 hover:border-slate-200'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePerm(p.value)}
                                className="accent-[#1a2f38]"
                              />
                              <span className="text-xs text-slate-700">{p.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1a2f38]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Speichern' : 'Rolle erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
